import React from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook, MapPin, Phone, Mail } from "lucide-react";
import { SITE_INFO, LOGO_URL } from "../mock";

export default function Footer() {
  return (
    <footer className="bg-black border-t border-gold/20 pt-16 pb-8 mt-24">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12">
          {/* Brand */}
          <div>
            <div className="relative inline-block mb-4">
              <div
                className="absolute inset-0 -m-6"
                style={{
                  background: "radial-gradient(circle, rgba(245,230,200,0.5) 0%, rgba(245,230,200,0.25) 40%, rgba(201,169,97,0.1) 70%, transparent 90%)",
                  filter: "blur(15px)"
                }}
              />
              <img src={LOGO_URL} alt="Ludovic Garcia Photographe" className="relative h-28" />
            </div>
            <h3
              className="text-3xl text-gold mb-3 italic"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}
            >
              Garcia Ludovic
            </h3>
            <p className="text-gray-400 text-sm tracking-wider uppercase mb-4">Photographe</p>
            <p className="text-gray-300 text-sm leading-relaxed">
              Capturer l'instant, sublimer l'émotion.
              Basé à {SITE_INFO.location}, disponible dans toute la France.
            </p>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-gold uppercase tracking-wider text-sm mb-4">Contact</h4>
            <ul className="space-y-3 text-gray-300 text-sm">
              <li className="flex items-start gap-3">
                <MapPin size={16} className="text-gold mt-0.5 shrink-0" />
                <span>{SITE_INFO.location}, France</span>
              </li>
              <li className="flex items-start gap-3">
                <Phone size={16} className="text-gold mt-0.5 shrink-0" />
                <a href={`tel:${SITE_INFO.phone.replace(/\s/g, '')}`} className="hover:text-gold transition-colors">
                  {SITE_INFO.phone}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Mail size={16} className="text-gold mt-0.5 shrink-0" />
                <a href={`mailto:${SITE_INFO.email}`} className="hover:text-gold transition-colors break-all">
                  {SITE_INFO.email}
                </a>
              </li>
            </ul>
          </div>

          {/* Social & Links */}
          <div>
            <h4 className="text-gold uppercase tracking-wider text-sm mb-4">Réseaux sociaux</h4>
            <div className="flex flex-wrap gap-3 mb-6">
              <a
                href={SITE_INFO.social.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 border border-gold/40 hover:border-gold hover:bg-gold hover:text-black text-gold flex items-center justify-center transition-colors"
                aria-label="Instagram"
              >
                <Instagram size={18} />
              </a>
              <a
                href="https://facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 border border-gold/40 hover:border-gold hover:bg-gold hover:text-black text-gold flex items-center justify-center transition-colors"
                aria-label="Facebook"
              >
                <Facebook size={18} />
              </a>
              <a
                href={SITE_INFO.social.tiktok}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 border border-gold/40 hover:border-gold hover:bg-gold hover:text-black text-gold flex items-center justify-center transition-colors"
                aria-label="TikTok"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-.1z"/>
                </svg>
              </a>
              <a
                href={`https://www.snapchat.com/add/${SITE_INFO.social.snapchat}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 border border-gold/40 hover:border-gold hover:bg-gold hover:text-black text-gold flex items-center justify-center transition-colors"
                aria-label="Snapchat"
                title={`Snapchat : ${SITE_INFO.social.snapchat}`}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12.206.793c.99 0 4.347.276 5.93 3.821.529 1.193.403 3.219.299 4.847l-.003.06c-.012.18-.022.345-.03.51.075.045.203.09.401.09.3-.016.659-.12 1.033-.301.165-.088.344-.104.464-.104.182 0 .359.029.509.09.45.149.734.479.734.838.015.449-.39.839-1.213 1.168-.089.029-.209.075-.344.119-.45.135-1.139.36-1.333.81-.09.224-.061.524.12.868l.015.015c.06.136 1.526 3.475 4.791 4.014.255.044.435.27.42.509 0 .075-.015.149-.045.225-.24.569-1.273.988-3.146 1.271-.059.091-.12.375-.164.57-.029.179-.074.36-.134.553-.076.271-.27.405-.555.405h-.03c-.135 0-.313-.031-.538-.074-.36-.075-.765-.135-1.273-.135-.3 0-.599.015-.913.074-.6.104-1.123.464-1.723.884-.853.599-1.826 1.288-3.294 1.288-.06 0-.119-.015-.18-.015h-.149c-1.468 0-2.427-.675-3.279-1.288-.599-.42-1.107-.779-1.707-.884-.314-.045-.629-.074-.928-.074-.54 0-.958.089-1.272.149-.211.043-.391.078-.54.078-.374 0-.523-.224-.583-.42-.061-.192-.09-.389-.135-.567-.046-.181-.105-.494-.166-.57-1.918-.222-2.95-.642-3.189-1.226-.031-.063-.052-.15-.055-.225-.015-.243.165-.465.42-.509 3.264-.54 4.73-3.879 4.791-4.02l.016-.029c.18-.345.224-.645.119-.869-.195-.434-.884-.658-1.332-.809-.121-.029-.24-.074-.346-.119-1.107-.435-1.257-.93-1.197-1.273.09-.479.674-.793 1.168-.793.146 0 .27.029.383.074.42.194.789.3 1.104.3.234 0 .384-.06.465-.105l-.046-.569c-.098-1.626-.225-3.651.307-4.837C7.392 1.077 10.739.807 11.727.807l.419-.015h.06z"/>
                </svg>
              </a>
            </div>
            <div className="space-y-1 text-xs text-gray-400">
              <p>Instagram : {SITE_INFO.social.instagramHandle}</p>
              <p>Snap : {SITE_INFO.social.snapchat}</p>
              <p>TikTok : {SITE_INFO.social.tiktokHandle}</p>
              <p>Facebook : {SITE_INFO.social.facebook}</p>
              <p>Page : {SITE_INFO.social.facebookPage}</p>
              <p>Google : {SITE_INFO.social.google}</p>
            </div>
          </div>
        </div>

        {/* Nav links */}
        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs text-gray-400 uppercase tracking-wider">
            <Link to="/" className="hover:text-gold transition-colors">Accueil</Link>
            <Link to="/galerie" className="hover:text-gold transition-colors">Galerie</Link>
            <Link to="/tarifs" className="hover:text-gold transition-colors">Tarifs</Link>
            <Link to="/cartes-cadeaux" className="hover:text-gold transition-colors">Cartes cadeaux</Link>
            <Link to="/reservation" className="hover:text-gold transition-colors">Réservation</Link>
            <Link to="/contact" className="hover:text-gold transition-colors">Contact</Link>
            <Link to="/mentions-legales" className="hover:text-gold transition-colors">Mentions légales & CGV</Link>
          </div>
          <p className="text-xs text-gray-500">
            © {new Date().getFullYear()} Garcia Ludovic Photographe. Tous droits réservés.
          </p>
        </div>
      </div>
    </footer>
  );
}
